"use client"

import { cn } from "@ui/utils"
import { ArrowRight, Loader2 } from "lucide-react"

interface PlanButtonProps {
  title: string
  price: string
  period: string
  badge?: string
  className?: string
  variant?: "list" | "card"
  loading?: boolean
  disabled?: boolean
  description?: string
  onClick: () => void
}

export function PlanButton({
  title,
  price,
  period,
  badge,
  className,
  variant = "list",
  description,
  loading,
  disabled,
  onClick,
}: PlanButtonProps) {
  if (variant === "card") {
    return (
      <button
        type="button"
        onClick={onClick}
        disabled={disabled}
        className={cn(
          "group relative flex min-h-44 w-full flex-col rounded-lg border px-5 py-4 text-left transition-colors",
          "border-border hover:border-border-accent hover:bg-tinted",
          "disabled:pointer-events-none disabled:opacity-50",
          className
        )}
      >
        <div className="flex min-h-6 items-start justify-between gap-3">
          <span className="text-foreground text-lg font-medium">{title}</span>
          {badge && (
            <span className="bg-brand text-foreground! shrink-0 rounded-full px-3 py-2 text-sm leading-none font-medium whitespace-nowrap">
              {badge}
            </span>
          )}
        </div>

        <div className="mt-2 flex flex-wrap items-end gap-x-1 gap-y-0.5">
          <span className="text-3xl leading-none font-semibold">{price}</span>
          <span className="text-tertiary-foreground pb-0.5 text-sm whitespace-nowrap">{period}</span>
        </div>

        {description && <p className="text-tertiary-foreground mt-3 leading-snug">{description}</p>}

        <div className="mt-auto flex justify-end pt-2">
          <span className="border-border bg-background group-hover:border-border-accent group-hover:bg-background flex size-8 items-center justify-center rounded-full border transition-colors">
            {loading ? (
              <Loader2 className="text-muted-foreground size-4 animate-spin" />
            ) : (
              <ArrowRight className="text-muted-foreground size-4" />
            )}
          </span>
        </div>
      </button>
    )
  }

  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className={cn(
        "relative w-full rounded-lg border-2 p-4 text-left transition-colors",
        "border-border hover:border-border-accent hover:bg-tinted",
        "disabled:pointer-events-none disabled:opacity-50",
        className
      )}
    >
      <div className="flex items-start justify-between">
        <div>
          <span className="text-muted-foreground text-sm font-medium">{title}</span>
          <div className="flex items-baseline gap-1">
            <span className="text-2xl font-bold">{price}</span>
            <span className="text-muted-foreground text-sm">{period}</span>
          </div>
        </div>
        <div className="mt-1 flex items-center gap-2">
          {badge && (
            <span className="bg-primary text-primary-foreground rounded-full px-2 py-0.5 text-xs font-medium whitespace-nowrap">
              {badge}
            </span>
          )}
          {loading && <Loader2 className="text-muted-foreground size-5 animate-spin" />}
          {!loading && <ArrowRight className="text-muted-foreground size-5" />}
        </div>
      </div>
      {description && <p className="text-muted-foreground mt-1 text-xs">{description}</p>}
    </button>
  )
}
